import { cva } from "class-variance-authority";
import { type ClassValue, clsx } from "clsx";
import { format } from "date-fns";
import { twMerge } from "tailwind-merge";
import { z } from "zod";
import {
  GroupedQueryClauseDto,
  QueryRequestDto,
  SingleQueryClauseDto,
} from "~/components/pages/reports_old/query/create/type";
import {
  SavedQueryClauseById,
  SavedQueryClauseParent,
} from "~/components/pages/reports_old/query/types";
import { ChartConfig } from "~/components/ui";
import { CollectionItemDto } from "~/redux/api/openapi.generated";
import { navigationItems } from "~/routes/navigation";

import {
  DATE_TIME,
  DATE_TIME_NULLABLE,
  DEFAULT_API_PAYLOAD,
  GROUPED_CLAUSE,
  MODELTYPES,
  Option,
  SINGLE_CLAUSE,
  SYSTEM_BOOLEAN,
} from "./constants";
import { AttachmentInput, Status } from "./types";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function waitForTimeout(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export const getFormData = (data: any) => {
  const formData = new FormData();
  Object.keys(data).forEach((key) => {
    formData.append(key, data[key] ?? "");
  });
  return formData;
};
export const constructPayload = <T>(data: any): T => {
  return {
    ...DEFAULT_API_PAYLOAD,
    ...data,
  };
};
export const getStatusColor = (status: string) => {
  switch (status) {
    case Status.New:
      return "bg-neutral-300 text-neutral-600";
    case Status.Open:
      return "bg-accent-200 text-accent-900";
    case Status.InProgress:
      return "bg-info-100 text-info-500";
    case Status.Resolvable:
      return "bg-success-100 text-success-500";
    case Status.Rejected:
      return "bg-danger-200 text-danger-500";
    case Status.NonCompliant:
      return "bg-warning-200 text-warning-800";
    case Status.Compliant:
      return "bg-success-200 text-success-800";
    case Status.Overdue:
      return "bg-danger-500 text-white";
    case Status.Closed:
      return "bg-success-500 text-white";
    case Status.Voided:
      return "bg-neutral-200 text-neutral-500";
    case Status.Draft:
      return "bg-neutral-100 border border-dashed border-neutral-300 text-neutral-500";
    default:
      return "bg-gray-100 text-neutral-500";
  }
};

export const getLowerWord = (str: string) =>
  str.replace(/\s+/g, "").toLowerCase();

export const getJoinedWord = (str: string) =>
  str
    .toLowerCase()
    .split(" ")
    .map((word, index) =>
      index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join("");
export const fullname = (name?: string, surname?: string) =>
  [name, surname].filter(Boolean).join(" ");
export const getInitials = (name: string) => {
  if (!name || typeof name !== "string") return "";

  const parts = name.trim().split(/\s+/);

  if (parts.length === 0) return "";

  const initials =
    (parts[0][0]?.toUpperCase() ?? "") +
    (parts[parts.length - 1][0]?.toUpperCase() ?? "");

  return initials;
};

interface cleanProps {
  userId?: string;
  name?: string;
  injuryCaseIds: string[];
  isFatality: boolean;
  internal: boolean;
}

export const cleanedData = (data: cleanProps[]) =>
  data?.filter((parties) => {
    return parties.name || parties.userId;
  });

export const formatTime = (hours: number, minutes: number) => {
  let result = "";

  if (hours > 0) {
    result += `${hours} hour${hours > 1 ? "s" : ""}`;
  }

  if (minutes > 0) {
    // Add a space if hours is included
    if (hours > 0) result += " ";
    result += `${minutes} minute${minutes > 1 ? "s" : ""}`;
  }

  return result.trim(); // Ensure there are no extra spaces
};

export const formatClock = (hours: number, minutes: number, light: boolean) => {
  const result = `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")} ${light ? "AM" : "PM"}`;
  return result.trim(); // Ensure there are no extra spaces
};

export const getListsForOptions = (array: CollectionItemDto[]) =>
  array?.map((item) => ({
    label: item.name,
    value: item.id,
  })) as Option[];

export const avatarStackVariants = cva("flex", {
  variants: {
    orientation: {
      vertical: "flex-row",
      horizontal: "flex-col",
    },
    spacing: {
      sm: "-space-x-5 -space-y-5",
      md: "-space-x-4 -space-y-4",
      lg: "-space-x-3 -space-y-3",
      xl: "-space-x-2 -space-y-2",
    },
  },
  defaultVariants: {
    orientation: "vertical",
    spacing: "md",
  },
});

export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
}

export const getModelFindingUrl = (
  modelType: string,
  modelId: string,
  multipleFindings: boolean,
) => {
  return multipleFindings
    ? `/${modelType}/${modelId}/findings`
    : `/findings/${modelId}/detail`;
};
export const getModelActionsUrl = (modelType: string, modelId: string) => {
  switch (modelType) {
    case MODELTYPES.ACTIONS:
      return `/hse/actions`;
    case MODELTYPES.ACTION_ENTRY:
      return `/hse/actions`;
    case MODELTYPES.INCIDENT:
      return `/incidents/view/${modelId}`;
    case MODELTYPES.OBSERVATION:
      return `/observations/view/${modelId}`;
    case MODELTYPES.AUDITS:
      return `/audits/view/${modelId}`;
    case MODELTYPES.INSPECTION:
      return `/inspections/view/${modelId}`;
    case MODELTYPES.CERTIFICATION:
      return `/compliance/personnel/certifications/${modelId}/detail`;
    case MODELTYPES.REGULATION:
      return `/compliance/organizational/regulations/${modelId}/detail`;
    case MODELTYPES.ELIGIBILITY_PERMIT:
      return `/compliance/personnel/eligibility-permits/${modelId}/detail`;
    case MODELTYPES.COMPETENCE_PERMIT:
      return `/compliance/personnel/competence-permits/${modelId}/detail`;
    case MODELTYPES.COMPANY_WIDE_PERMIT:
      return `/compliance/organizational/company-wide-permits/${modelId}/detail`;
    case MODELTYPES.SITE_SPECIFIC_PERMIT:
      return `/compliance/organizational/site-specific-permits/${modelId}/detail`;
    case MODELTYPES.FINDINGS:
      return `/findings/${modelId}/detail`;
    case MODELTYPES.SITE:
      return `/sites/${modelId}`;
    case MODELTYPES.MEETING_ENTRY:
      return `/hse/records/meeting/${modelId}`;
    default:
      return "/";
  }
};

/** Maps raw API modelType strings to user-facing display labels.
 * PascalCase values from the API (e.g. "CompanyWidePermit") are converted to
 * properly spaced/hyphenated labels (e.g. "Company-Wide Permit").
 * All unrecognised types pass through unchanged so existing labels (Audit,
 * Observation, etc.) are unaffected.
 */
const MODEL_TYPE_DISPLAY_LABELS: Record<string, string> = {
  EligibilityPermit: "Eligibility Permit",
  CompetencePermit: "Competence Permit",
  CompanyWidePermit: "Company-Wide Permit",
  SiteSpecificPermit: "Site-Specific Permit",
  EligibilityPermitDocument: "Eligibility Permit Document",
  CompetencePermitDocument: "Competence Permit Document",
  CompanyWidePermitDocument: "Company-Wide Permit Document",
  SiteSpecificPermitDocument: "Site-Specific Permit Document",
  WorkHoursRegister: "Work Hours Register",
};

export function getModelTypeLabel(modelType: string | undefined): string {
  if (!modelType) return "";
  return MODEL_TYPE_DISPLAY_LABELS[modelType] ?? modelType;
}

export interface PermissionChild {
  key: string;
  name: string;
  description: string;
  module: string;
  hasOptions: boolean;
  types: string[];
}

export interface Permission {
  section: string;
  isActive: boolean;
  children: PermissionChild[];
}
const FullAccess = "Full Access";
const Self = "Self";
const Direct = "Direct";
const Indirect = "Indirect";
export const permissionOptions = [FullAccess, Self, Direct, Indirect];
export const hasOptions = [FullAccess];
export const groupByModule = (children: PermissionChild[]) => {
  return children.reduce(
    (acc, child) => {
      acc[child.module] = acc[child.module] || [];
      acc[child.module].push(child);
      return acc;
    },
    {} as Record<string, PermissionChild[]>,
  );
};

export const removeDuplicateTypes = (data: Permission[]): Permission[] => {
  return data.map((section) => ({
    ...section,
    children: section.children.map((child) => ({
      ...child,
      types: [...new Set(child.types)], // Remove duplicate values
    })),
  }));
};

export function toSentenceCase(text: string | undefined): string {
  if (!text) return "";

  const trimmedText = text.trim().toLowerCase();

  return trimmedText.charAt(0).toUpperCase() + trimmedText.slice(1);
}

export const isImage = (fileName: string) => {
  const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".bmp", ".webp"];
  return imageExtensions.some((ext) => fileName.toLowerCase().endsWith(ext));
};

export const isDocument = (fileName: string) => {
  const documentExtensions = [".pdf", ".doc", ".docx", ".xls", ".xlsx", ".txt"];
  // console.log(fileName, "fileName");
  return documentExtensions.some((ext) => fileName.toLowerCase().endsWith(ext));
};

// export const browserKey = (item: string) => {
//   const result = item.replace(/[\s\-+]+/g, "_"); // Replace spaces, hyphens, and plus signs with underscores
//   return result;
// };
export const browserKey = (item: string) => {
  const result = item.replace(/[\s\-+/]+/g, "_"); // Replace spaces, hyphens, plus signs, and slashes with underscores
  return result;
};
export const formatTitle = (item: string): string => {
  return item.replace(/_/g, " "); // Replace all underscores with spaces
};
interface IChartConfig<T> {
  config: ChartConfig;
  data: T[];
}
const colorVariables = [
  "chart-1",
  "chart-2",
  "chart-3",
  "chart-4",
  "chart-5",
  "chart-6",
  "chart-7",
  "chart-8",
  "chart-9",
  "chart-10",
  "chart-11",
  "chart-12",
  "chart-13",
  "chart-14",
  "chart-15",
  "chart-16",
  "chart-17",
  "chart-18",
  "chart-19",
  "chart-20",
  "chart-21",
  "chart-22",
  "chart-23",
  "chart-24",
  "chart-25",
  "chart-26",
  "chart-27",
  "chart-28",
  "chart-29",
  "chart-30",
];
export const generateChartConfig = <T extends { name?: string }>({
  data,
  config,
}: IChartConfig<T>) => {
  if (config.value) {
    const chartConfig = config as {
      [key: string]: { label?: string; color: string };
    };

    data?.forEach((item, index: number) => {
      const display = item.name;
      const ky = browserKey(item?.name as string);
      chartConfig[ky] = {
        label: display,
        color: display
          ? `var(--${colorVariables[index % colorVariables.length]})`
          : "hsl(var(--empty-chart))",
      };
    });
    return chartConfig;
  } else {
    return config;
  }
};

export const calculateTextColor = (hexColor: string): string => {
  // Remove the hash if present
  const color = hexColor.startsWith("#") ? hexColor.slice(1) : hexColor;

  // Convert to RGB
  const r = parseInt(color.slice(0, 2), 16);
  const g = parseInt(color.slice(2, 4), 16);
  const b = parseInt(color.slice(4, 6), 16);

  // Calculate relative luminance
  const luminance =
    0.2126 * (r / 255) + 0.7152 * (g / 255) + 0.0722 * (b / 255);

  // Return white for dark background and black for light background
  return luminance < 0.5 ? "white" : "black";
};

export const capitalizeFirstChar = (word: string): string => {
  if (!word) return ""; // Handle empty or undefined input
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
};

type RecordItem = {
  key: string;
  name: string;
  description: string;
  module: string;
  hasOptions: boolean;
  types: any[];
};

type Section = {
  section: string;
  isActive: boolean;
  children: RecordItem[];
};

export const isSelfPlusOtherOrNonSelf = (accessList: string[]): boolean => {
  return !(accessList.length === 1 && accessList.includes(Self));
};

export const findRecordWithFullAccess = (
  sections: Section[],
  key: string,
): RecordItem | null => {
  if (!sections || !Array.isArray(sections)) return null;
  for (const section of sections) {
    if (!section || !section.children) continue;
    for (const child of section.children) {
      if (child.key === key) {
        // Check if the types array includes FullAccess
        if (
          child.types &&
          (child.types.includes(FullAccess) ||
            child.types.includes(Self) ||
            child.types.includes(Direct) ||
            child.types.includes(Indirect))
        ) {
          return child;
        }
      }
    }
  }
  return null; // Return null if the key is not found or FullAccess is not in types
};
export const findRecordWithSpecifiedAccess = (
  sections: Section[],
  key: string,
  access: string,
): boolean => {
  if (!sections || !Array.isArray(sections)) return false;
  for (const section of sections) {
    if (!section || !section.children) continue;
    for (const child of section.children) {
      if (child.key === key) {
        // Check if the types array includes FullAccess
        if (child.types && child.types.includes(access)) {
          return true;
        }
      }
    }
  }
  return false; // Return null if the key is not found or FullAccess is not in types
};

export const findRecordWithNotIncludedAccess = (
  sections: Section[],
  key: string,
  access: string,
): boolean => {
  if (!sections || !Array.isArray(sections)) return false;
  for (const section of sections) {
    if (!section || !section.children) continue;
    for (const child of section.children) {
      if (child.key === key) {
        console.log(child.types, access, "access, types");
        // Check if the types array includes FullAccess
        if (child.types && !child.types.includes(access)) {
          return true;
        }
      }
    }
  }
  return false; // Return null if the key is not found or FullAccess is not in types
};

export const findRecordWithNotIncludedSelfAccess = (
  sections: Section[],
  key: string,
): boolean => {
  if (!sections || !Array.isArray(sections)) return false;
  for (const section of sections) {
    if (!section || !section.children) continue;
    for (const child of section.children) {
      if (child.key === key) {
        // console.log(child.types, access, "access, types");
        // Check if the types array includes FullAccess
        if (child.types) {
          return isSelfPlusOtherOrNonSelf(child.types);
        }
        // if (!child.types.includes(access)) {
        //   return true;
        // }
      }
    }
  }
  return false; // Return null if the key is not found or FullAccess is not in types
};

export const findIcon = (icon: string, title: string) => {
  const byTitle = navigationItems.find((item) => item.title === title);
  if (byTitle) return byTitle;
  return navigationItems.find((item) => item.icon === icon);
};

export const findTopIndex = (arr: number[]) => {
  const hasZero = arr.includes(0);
  const smallest = Math.min(...arr);
  return hasZero ? 0 : smallest;
};

export const replaceItemAtIndex = (
  arr: SingleQueryClauseDto[],
  index: number,
  newValue: SingleQueryClauseDto,
) => {
  if (index >= 0 && index < arr.length) {
    return [
      ...arr.slice(0, index), // Elements before the index
      newValue, // New item to insert
      ...arr.slice(index), // Elements after the index
    ];
  }
};

export const handleDownload = async (
  files: { id: string; name: string; link: string }[],
): Promise<void> => {
  files.forEach(async (file) => {
    // Fetch file content
    const response = await fetch(file.link);
    // Check if the response was successful
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const blob = await response.blob();

    // Create downloadable link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = file.name;

    // Trigger download
    document.body.appendChild(link);
    link.click();

    // Clean up
    window.URL.revokeObjectURL(url);
    document.body.removeChild(link);
  });
};

export const getFieldForEditQuery = (input: string) => {
  return input
    .split(" ") // Split by spaces
    .map(
      (word, index) =>
        index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1), // Capitalize second word onwards
    )
    .join("");
};

export const getScreenForQuery = (input: string) => {
  if (!input) return ""; // Handle empty or null input

  // Convert the input to lowercase
  const lowerCased = input.toLowerCase();

  // Check if the input contains "types"
  if (lowerCased.includes("types")) {
    // Extract the first word and pluralize it
    return lowerCased.split(" ")[0] + "s";
  }

  // Otherwise, return the input in lowercase
  return lowerCased;
};

export const getItemTypeForEdit = (input: string) => {
  if (!input) return "";

  // If it's a single word with no camelCase/PascalCase, check if it needs pluralization
  if (!/[a-z][A-Z]/.test(input)) {
    return input.endsWith("s") ? input : input + "s"; // Pluralize single words if not ending in "s"
  }

  // Convert camelCase or PascalCase to words (split before uppercase letters)
  const words = input.replace(/([a-z])([A-Z])/g, "$1 $2").split(" ");

  // Only pluralize the last word if there's more than one word
  const lastWord = words[words.length - 1];
  if (!lastWord.endsWith("s")) {
    words[words.length - 1] = lastWord + "s"; // Pluralize only the last word
  }

  return words.join(" ");
};

export const getItemType = (input: string) => {
  return input
    ?.split(" ") // Split by spaces
    ?.map((word) => {
      // Remove trailing "s""
      const singularWord = word?.endsWith("s") ? word?.slice(0, -1) : word;
      // Capitalize first letter
      return singularWord?.charAt(0)?.toUpperCase() + singularWord?.slice(1);
    })
    ?.join(""); // Join without spaces or hyphens
};

export const getParameterName = (input: string) => {
  const words = input?.split(" ");
  if (words.length === 1) {
    return input;
  }
  return words.join("");
};

export const parseOperatorSyntax = (operatorSyntax: string) => {
  if (operatorSyntax?.includes(":") === false) {
    return operatorSyntax;
  }
  return operatorSyntax?.split(":")[1];
};

export const findParentNode = (queries: QueryRequestDto) => {
  const clauses = queries?.queries;
  if (clauses?.length >= 2) {
    if (
      clauses[0]?.grouped === SINGLE_CLAUSE &&
      clauses[1]?.grouped === SINGLE_CLAUSE
    ) {
      return clauses[1]; // Second clause is the parent
    } else if (
      clauses[0]?.grouped === SINGLE_CLAUSE &&
      clauses[1]?.grouped === GROUPED_CLAUSE
    ) {
      return clauses[1]?.groupedClauses && clauses[1]?.groupedClauses[0]; // First clause within the clauseIds of the second item
    } else if (clauses[0]?.grouped === GROUPED_CLAUSE) {
      return clauses[0]?.groupedClauses && clauses[0]?.groupedClauses[0];
    }
  }
  return null; // Default to null if no matching conditions
};

export const mapClauseToQuery = (
  queryClause: QueryRequestDto,
  singleClauseCount: number,
) => {
  const clause = queryClause?.queries[0];
  const isGrouped = clause?.grouped === GROUPED_CLAUSE;
  const singleWithChildren =
    !isGrouped && singleClauseCount >= 3 ? true : false;
  let type = isGrouped ? 1 : 0;
  const clauseObject = isGrouped
    ? null
    : {
        parameterType: getItemType(clause?.itemType?.label),
        parameterName: getParameterName(clause?.field?.value),
        parameterDisplayName: clause?.field?.label,
        fieldType: clause?.field?.data?.fieldType,
        operator: {
          name: clause?.operator?.label,
          operatorSyntax: parseOperatorSyntax(clause?.operator?.value), // Static or derive as needed
        },
        fieldValue: {
          value:
            typeof clause?.value === "string"
              ? clause?.value
              : clause?.value instanceof Date
                ? clause?.value?.toISOString()
                : Array.isArray(clause?.value)
                  ? clause?.value?.map((item: any) => item?.value).join(",")
                  : (clause?.value as any)?.value,
          displayName:
            typeof clause?.value === "string"
              ? clause?.value
              : clause?.value instanceof Date
                ? clause?.value?.toISOString()
                : Array.isArray(clause?.value)
                  ? clause?.value?.map((item: any) => item?.label).join(", ")
                  : (clause?.value as any)?.label,
        },
      };

  if (singleWithChildren) {
    type = 1; // Set type to 1 for subsequent SINGLE_CLAUSE types
  }

  return {
    type: type,
    clause: isGrouped ? null : singleWithChildren ? null : clauseObject,
    children: isGrouped
      ? clause?.groupedClauses?.map(
          (innerClause): unknown | QueryRequestDto => {
            const nestedClause = { queries: [innerClause] };
            return mapClauseToQuery(nestedClause, singleClauseCount);
          },
        )
      : singleWithChildren
        ? [
            {
              type: 0,
              clause: clauseObject,
              children: null,
              groupOperator: clause?.action?.label,
            },
          ]
        : null,
    groupOperator:
      isGrouped && clause?.groupedClauses
        ? clause?.groupedClauses[1]?.action?.label
        : singleWithChildren
          ? clause?.action?.label
          : !clause?.action?.label
            ? null
            : clause?.action?.label,
  };
};

export const populateQueryClauseForEdit = (
  item: SavedQueryClauseParent | SavedQueryClauseById,
  clause_type: string,
  values?: Option[],
  queryData?: { itemType: string; data: [] }[],
) => {
  const getItemValue = () => {
    if (item?.clause?.fieldType === DATE_TIME_NULLABLE) {
      return format(item?.clause?.fieldValue?.displayName, "yyy-MMM-dd");
    }
    if (item?.clause?.fieldType === DATE_TIME) {
      return format(item?.clause?.fieldValue?.displayName, "yyy-MMM-dd");
    }
    return item?.clause?.fieldValue?.displayName as string;
  };
  return {
    checked: false,
    grouped: clause_type,
    action: item?.groupOperator
      ? {
          value: item?.groupOperator,
          label: item?.groupOperator,
        }
      : { value: "And", label: "" }, //dummy data to pass validation
    itemType: {
      value: getItemTypeForEdit(item?.clause?.parameterType as string),
      label: getItemTypeForEdit(item?.clause?.parameterType as string),
    },
    field: {
      value: item?.clause?.parameterName as string,
      label: item?.clause?.parameterDisplayName as string,
      data: {
        value: values ? values : null,
        label: "field",
        dateTime: "field",
        fieldType: item?.clause?.fieldType as string,
        relatedEntityType:
          item?.clause?.fieldType === SYSTEM_BOOLEAN
            ? null
            : item?.clause?.fieldType === DATE_TIME
              ? null
              : item?.clause?.fieldType === DATE_TIME_NULLABLE
                ? null
                : (item?.clause?.fieldType as string),
      },
    },
    operator: {
      value: item?.clause?.operator?.operatorSyntax as string,
      label: item?.clause?.operator?.name as string,
    },
    value:
      item?.clause?.fieldType === DATE_TIME
        ? getItemValue()
        : item?.clause?.fieldType === DATE_TIME_NULLABLE
          ? getItemValue()
          : item?.clause?.fieldType === SYSTEM_BOOLEAN
            ? getItemValue()
            : !item?.clause?.fieldType
              ? getItemValue()
              : {
                  label: getItemValue() as string,
                  value: item?.clause?.fieldValue?.value as string,
                },
    queryData: queryData ?? [],
    groupedClauses: [],
  };
};

export const returnNodeOperator = (
  extractedQuery: GroupedQueryClauseDto[],
  queryDetails: SavedQueryClauseById,
  parentClause: GroupedQueryClauseDto,
) => {
  const finalExtracted = extractedQuery?.map((item) => {
    if (item === parentClause && item?.grouped === SINGLE_CLAUSE) {
      return {
        ...item,
        action: {
          label: queryDetails?.groupOperator as string,
          value: queryDetails?.groupOperator as string,
        },
      };
    } else if (item?.grouped === GROUPED_CLAUSE) {
      return {
        ...item,
        groupedClauses: item?.groupedClauses?.map((inneritem) => {
          if (inneritem === parentClause) {
            return {
              ...inneritem,
              action: {
                label: queryDetails?.groupOperator as string,
                value: queryDetails?.groupOperator as string,
              },
            };
          }
          return inneritem;
        }),
      };
    }

    return item;
  });

  return finalExtracted;
};

export const calculateFitAndOverflow = (
  options: Option[],
  maxCharacters: number,
): { fitItems: Option[]; overflowCount: number; currentTotal: number } => {
  // Function to calculate total length (label length + 3)
  const calculateTotalLength = (label: string): number => label.length + 4;
  const maxChars = maxCharacters - 5;

  const fitItems: Option[] = [];
  let currentTotal = 0;

  // Track the items that fit within the maxCharacters
  options.forEach((item) => {
    const itemLength = calculateTotalLength(item.label);
    if (currentTotal + itemLength <= maxChars) {
      fitItems.push(item);
      currentTotal += itemLength;
    }
  });

  // Calculate overflow count
  const overflowCount = options.length - fitItems.length;

  return { currentTotal, fitItems, overflowCount };
};

type ObjectType = {
  [key: number]: boolean;
};

interface DataProps {
  id: string;
}
export function getMatchingIds(
  array: DataProps[],
  object: ObjectType,
): string[] {
  const result: string[] = [];
  array.forEach((item, index) => {
    if (object[index] === true) {
      result.push(item.id);
    }
  });
  return result;
}
export const getCroppedImg = async (
  imageSrc: string,
  crop: any,
  fileName: string = "cropped-image.jpg", // Default file name
  format: string = "image/jpeg", // Default format
  quality: number = 0.9, // Default quality for JPEG
): Promise<File> => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.src = imageSrc;
    image.crossOrigin = "anonymous"; // To handle CORS issues

    image.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        reject(new Error("Failed to create canvas context"));
        return;
      }

      canvas.width = crop.width;
      canvas.height = crop.height;

      ctx.drawImage(
        image,
        crop.x,
        crop.y,
        crop.width,
        crop.height, // Source rectangle (crop area)
        0,
        0,
        crop.width,
        crop.height, // Destination rectangle (canvas)
      );

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Canvas is empty"));
            return;
          }

          // Convert the Blob to a File object
          const file = new File([blob], fileName, {
            type: format,
          });
          resolve(file);
        },
        format,
        quality,
      );
    };

    image.onerror = () => reject(new Error("Failed to load image"));
  });
};

const allowedMimeTypes = [
  "image/png",
  "image/jpeg", // covers both JPG and JPEG
  "image/webp",
  "application/pdf",
  "application/msword", // DOC
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // DOCX
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // XLSX
];

export const imageValidationSchema = z.any().refine(
  (attachment: AttachmentInput) => {
    const image = attachment;
    // If it's an array of blobs, validate each blob
    if (Array.isArray(image)) {
      return (
        image.length > 0 &&
        image.every(
          (blob) =>
            z
              .instanceof(Blob)
              .refine((blob) => allowedMimeTypes.includes(blob.type), {
                message:
                  "All attachments must be valid files (PNG, JPG, JPEG, WEBP, PDF, DOC, DOCX, XLSX)",
              })
              .safeParse(blob).success,
        )
      );
    }

    // If it's a FileList, validate each file
    if (image instanceof FileList) {
      const files = Array.from(image);
      return (
        files.length > 0 &&
        files.every(
          (file) =>
            z
              .instanceof(File)
              .refine((file) => allowedMimeTypes.includes(file.type), {
                message:
                  "All attachments must be valid files (PNG, JPG, JPEG, WEBP, PDF, DOC, DOCX, XLSX)",
              })
              .safeParse(file).success,
        )
      );
    }

    return false; // If none of the above conditions are satisfied
  },
  {
    message:
      "Invalid file. Please upload a valid file (PNG, JPG, JPEG, WEBP, PDF, DOC, DOCX, XLSX)",
  },
);
